<template>
  <MainLayout>
    <!-- Orders Sub Navigation -->
    <OrdersSubNavbar />

    <div class="tables-view space-y-4 sm:space-y-6 px-3 sm:px-6">
      <!-- Page Header -->
      <PageHeader
        :title="t('app.views.tables.title')"
        :subtitle="t('app.views.tables.subtitle')"
      >
        <template #actions>
          <BaseButton
            v-if="canManageTables"
            variant="primary"
            @click="openAddTableModal"
          >
            <template #icon>
              <PlusIcon
                class="h-5 w-5"
                aria-hidden="true"
              />
            </template>
            {{ t('app.views.tables.add_table') }}
          </BaseButton>
        </template>
      </PageHeader>

      <!-- States: Loading, Error, Empty -->
      <TableStates
        :loading="loading"
        :error="error"
        :is-empty="tables.length === 0 && !loading && !error"
        :can-manage-tables="canManageTables"
        @add-table="openAddTableModal"
      />

      <!-- Tables Grid -->
      <div
        v-if="!loading && !error && tables.length > 0"
        class="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5"
      >
        <TableCard
          v-for="table in tables"
          :key="table.id"
          :table="table"
          :is-selected="selectedTableId === table.id"
          :has-open-order="hasOpenOrder(table.id)"
          :can-manage-tables="canManageTables"
          :can-create-orders="canCreateOrders"
          :is-menu-open="menuStates[table.id]"
          @select="handleSelectTable"
          @new-order="handleNewOrder"
          @edit-order="handleEditOrder"
          @view-bill="handleViewBill"
          @toggle-occupancy="handleToggleOccupancy"
          @edit="handleEditTable"
          @delete="handleDeleteTable"
          @update:menu-open="menuStates[table.id] = $event"
        />
      </div>

      <!-- Add/Edit Table Modal -->
      <TableFormModal
        :is-open="showTableModal"
        :table="editingTableId ? tableToEdit : {}"
        :is-editing="editingTableId !== null"
        @close="closeTableModal"
        @save="handleSaveTable"
      />

      <TablesOrderModals
        :show-order-modal="showOrderModal"
        :selected-table-for-order="selectedTableForOrder"
        :order-modal-mode="orderModalMode"
        :order-to-edit="orderToEdit"
        :show-order-details-modal="showOrderDetailsModal"
        :selected-order-for-details="selectedOrderForDetails"
        @close-order-modal="closeOrderModal"
        @order-created="handleOrderCreated"
        @close-order-details-modal="closeOrderDetailsModal"
        @order-status-update="handleOrderStatusUpdate"
        @payment-completed="handlePaymentCompleted"
      />

      <!-- Limit Reached Modal -->
      <LimitReachedModal
        :is-open="showLimitModal"
        :message="limitMessage"
        :current-usage="currentUsage ?? undefined"
        :max-limit="maxLimit ?? undefined"
        resource-type="tables"
        @close="showLimitModal = false"
      />
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { useTables } from '@/composables/useTables';
import { useTableOrders } from '@/composables/useTableOrders';
import MainLayout from '@/components/layout/MainLayout.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import OrdersSubNavbar from '@/components/orders/OrdersSubNavbar.vue';
import TableCard from '@/components/tables/TableCard.vue';
import TableStates from '@/components/tables/TableStates.vue';
import TableFormModal from '@/components/tables/TableFormModal.vue';
import TablesOrderModals from '@/components/tables/TablesOrderModals.vue';
import { PlusIcon } from '@heroicons/vue/24/outline';
import LimitReachedModal from '@/components/subscription/LimitReachedModal.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import type { Table } from '@/services/tableService';
import { useTablesViewOrders } from '@/composables/useTablesViewOrders';

const { t } = useI18n();
const { canManageTables, canCreateOrders } = usePermissions();

// Composables
const {
  tables,
  loading,
  error,
  selectedTableId,
  fetchTables,
  createTable,
  updateTable,
  deleteTable,
  toggleOccupancy,
  selectTable,
  clearError
} = useTables();

const {
  hasOpenOrder,
  refreshOpenOrders,
  getOpenOrderForTable,
  getOrderDetails
} = useTableOrders();

// Table form state
const showTableModal = ref(false);
const editingTableId = ref<number | null>(null);
const tableToEdit = ref<Partial<Table>>({});

// Menu states for dropdown
const menuStates = ref<Record<number, boolean>>({});

// Limit reached modal state
const showLimitModal = ref(false);
const limitMessage = ref('');
const currentUsage = ref<number | null>(null);
const maxLimit = ref<number | null>(null);

const refreshTablesData = () => {
  fetchTables();
  refreshOpenOrders();
};

const {
  showOrderModal,
  selectedTableForOrder,
  orderModalMode,
  orderToEdit,
  showOrderDetailsModal,
  selectedOrderForDetails,
  handleNewOrder,
  handleEditOrder,
  handleViewBill,
  closeOrderModal,
  closeOrderDetailsModal,
  handleOrderCreated,
  handleOrderStatusUpdate,
  handlePaymentCompleted
} = useTablesViewOrders({
  getOpenOrderForTable,
  getOrderDetails,
  refreshTablesData
});

// Table modal handlers
const openAddTableModal = () => {
  tableToEdit.value = {};
  editingTableId.value = null;
  showTableModal.value = true;
};

const closeTableModal = () => {
  showTableModal.value = false;
  editingTableId.value = null;
  tableToEdit.value = {};
  clearError();
};

const handleSaveTable = async (tableData: Partial<Table>) => {
  let result;
  if (editingTableId.value !== null) {
    result = await updateTable(editingTableId.value, tableData);
  } else {
    result = await createTable(tableData);
  }

  if (result.success) {
    closeTableModal();
  } else if (result.isLimitError) {
    // Show limit modal
    limitMessage.value = result.message || '';
    currentUsage.value = result.currentUsage ?? null;
    maxLimit.value = result.maxLimit ?? null;
    showLimitModal.value = true;
    closeTableModal();
  }
  // Error is already set in composable
};

// Table card event handlers
const handleSelectTable = (table: Table) => {
  selectTable(table.id);
};

const handleEditTable = (table: Table) => {
  tableToEdit.value = table;
  editingTableId.value = table.id;
  showTableModal.value = true;
};

const handleDeleteTable = async (table: Table) => {
  if (!confirm(t('app.views.tables.confirm_delete', { number: table.number }) || `Are you sure you want to delete Table #${table.number}?`)) {
    return;
  }

  await deleteTable(table.id);
};

const handleToggleOccupancy = async (table: Table) => {
  await toggleOccupancy(table.id, table.is_occupied);
};

// Initialize component
onMounted(() => {
  refreshTablesData();
});
</script>
